import { Bell } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockField, MockButton, MockTable } from "../shared";

export default function AdminReminderScheduleSection() {
  return (
    <div data-testid="guide-admin-reminder-schedule">
      <PageHeading
        icon={Bell}
        title="Reminder Schedule"
        description="When reminder emails go out before a due date (which you can change), the fixed schedule for overdue chasers (which you can only view), and a log of every reminder the system has sent."
      />

      <Callout variant="info" title="In plain words">
        This screen controls the nudge emails. You decide how many days <span className="text-white">before</span> a deadline people get reminded. The chaser emails that go out <span className="text-white">after</span> a deadline follow a fixed pattern you can read but not change. At the bottom you can see a log of every reminder that was sent.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="it_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_reminder_schedule</span>. Saving requires step-up re-authentication.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it to tune how early people are reminded before deadlines, to check who receives reminders, or to investigate why a reminder did or didn't go out (using the send log).
        </p>
      </Section>

      <Section title="Setting the pre-due reminders">
        <Diagram
          frameLabel="Admin → Reminder Schedule"
          legend={[
            <>Edit the <span className="text-white">offsets</span> — the days before the due date when reminders fire (default 14, 7, 1).</>,
            <>Optionally type a <span className="text-white">reason for change</span> for the audit record.</>,
            <>Click <span className="text-white">Save</span> and confirm with your password (step-up).</>,
          ]}
        >
          <MockField label="Offsets (days before due date)" value="14, 7, 1" marker={1} />
          <MockField label="Reason for change" placeholder="e.g. narrowed to 7/1 for trial week" marker={2} />
          <MockButton label="Save" marker={3} />
        </Diagram>
        <Steps steps={[
          <>Edit the offsets list and the optional reason.</>,
          <>Click <span className="text-white">Save</span>. Enter your password (step-up).</>,
          <>The new schedule applies on the next scheduler tick (within ~60 minutes). The change is audit-logged.</>,
        ]} />
      </Section>

      <Section title="Pre-due offsets (configurable)">
        <div className="space-y-2">
          <Field name="Offsets (days before due date)" required="required">
            <p>List of integers, default <span className="text-white">14, 7, 1</span>. Each is the number of calendar days before <span className="text-white">due_date</span> at which the system sends a reminder.</p>
            <p>Maximum <span className="text-white">10</span> offsets. Each offset must be between <span className="text-white">1</span> and <span className="text-white">365</span> days.</p>
            <p>Duplicates are rejected. Order does not matter — the scheduler sorts internally.</p>
          </Field>
          <Field name="Reason for change" required="optional"><p>Free-text note saved on the audit row, e.g. "trial week, narrowed to 7/1".</p></Field>
        </div>
      </Section>

      <Section title="Past-due cadence (fixed in code, shown read-only)">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li><span className="text-white">+3, +6, +9, +12</span> working days past due — successive escalation reminders.</li>
          <li><span className="text-white">Final</span> reminder fires on a configured working-day boundary (constant <span className="text-white">REMINDER_OVERDUE_FINAL_DAY</span>).</li>
          <li>From <span className="text-white">REMINDER_OVERDUE_MD_CC_DAY</span> working days past due onward, SDF MD is added to CC on every overdue email.</li>
        </ul>
      </Section>

      <Section title="Recipients">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1.5">
          <li><span className="text-white">Main task</span> — TO: current responsible holder. CC: original assignee (the owner).</li>
          <li><span className="text-white">Sub-task</span> — TO: sub-task assignee. CC: original main-task assignee (the owner).</li>
          <li>Routing uses the team-member email binding configured in Admin → Team Members / User Accounts.</li>
        </ul>
      </Section>

      <Section title="Clock-stop statuses (no reminders fire)">
        <p className="text-xs text-white/65">Items in any of these statuses are skipped by the scheduler:</p>
        <ul className="list-disc list-inside text-xs text-white/65 ml-2 space-y-0.5">
          <li><span className="text-white">CLOSED</span>, <span className="text-white">CANCELLED</span>, <span className="text-white">SENT_UPWARD</span>, <span className="text-white">AWAITING_RESPONSE</span>, <span className="text-white">SIGNED_OFF</span></li>
          <li>Soft-deleted items (any status).</li>
        </ul>
      </Section>

      <Section title="Reminder Send Log">
        <p className="text-xs text-white/65 leading-relaxed mb-3">Below the editor, every reminder fire is listed with: timestamp, slot kind (Pre-due / Overdue / Final), cadence offset (e.g. <span className="text-white">−7d</span> or <span className="text-white">+12d</span>), action item, recipient list, result (sent / failed / skipped with reason). Click a row to navigate to the parent item. There is also a manual-retry control on failed rows.</p>
        <MockTable
          columns={["When", "Kind", "Offset", "Item", "Result"]}
          rows={[
            ["08:00", "Pre-due", "−7d", "Q3 portfolio pack", <span className="text-emerald-300">sent</span>],
            ["08:00", "Overdue", "+12d", "IC minutes follow-up", <span className="text-red-300">failed</span>],
            ["08:00", "Pre-due", "−1d", "Board resolution 14", <span className="text-amber-300">skipped</span>],
          ]}
        />
      </Section>

      <Callout variant="warning" title={`What "skipped" means`}>
        Skipped rows are not failures. Common reasons: item is in a clock-stop status, item is soft-deleted, or no email address is bound to the recipient. Investigate persistent skips by clicking the row and checking the linked item.
      </Callout>
    </div>
  );
}
